import React, { useState, useEffect, useMemo } from 'react';
import './style.css';
import { useNavigate } from 'react-router-dom';
import axios from '../../axiosInstance';
import Grid from 'apollo-react/components/Grid';
import Button from 'apollo-react/components/Button';
import Typography from '../common/StyledTypography';
import { Form, withFormik } from 'formik';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { FormikTextField } from '../common/FormikComponent';
import Info from 'apollo-react-icons/Info';
import Tooltip from 'apollo-react/components/Tooltip';
import { forgotPasswordSchema } from '../../validation/auth';
import { toast } from 'react-toastify';
import AuthLayout from './AuthLayout';
import { useTranslation } from 'react-i18next';

const initialValues = {
  email: '',
};

const onFormSubmit = async (
  values,
  { setSubmitting, setFieldError, props }
) => {
  const { t } = props;
  try {
    const response = await axios.post('/api/v1/auth/forgot-password', values);

    if (response.data.success) {
      toast.success(t('forgotPassword.success'));
      setTimeout(() => {
        props.navigate('/login');
      }, 1000);
    } else {
      toast.error(response.data.message || t('forgotPassword.error'));
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.message || t('forgotPassword.error');
    toast.error(errorMessage);
    setFieldError('email', errorMessage);
  }
  setSubmitting(false);
};

const ForgetPassword = (props) => {
  const { setFieldValue, content } = props;
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    if (content) {
      Object.keys(content).forEach((key) => {
        // eslint-disable-next-line no-prototype-builtins
        if (initialValues.hasOwnProperty(key)) {
          setFieldValue(key, content[key]);
        }
      });
    }
    setLoading(false);
  }, [content, setFieldValue]);

  return loading ? (
    <Typography variant="bodySmall" style={{ margin: '8px 0' }}>
      {t('common.loading')}
    </Typography>
  ) : (
    <AuthLayout>
      <Grid
        item
        justifyContent="center"
        width="463px"
        position="relative"
        zIndex={1}
      >
        <Grid container justifyContent="left" className="signInTitleGrid">
          <div>
            <h2 className="signInTitle">{t('forgotPassword.title')}</h2>
            <p className="signInDesc signInDescForgotPassword">
              {t('forgotPassword.description')}
            </p>
          </div>
        </Grid>

        <Grid container className="formContainer">
          <Grid container justifyContent="center" sx={{ width: '100%' }}>
            <Grid size={12}>
              <Form noValidate>
                <FormikTextField
                  name="email"
                  type="email"
                  label={
                    <span className="formLabelWithIcon">
                      {t('forgotPassword.emailLabel')}
                      <Tooltip
                        title={t('forgotPassword.emailTooltip')}
                        placement="top"
                      >
                        <Info
                          fontSize="extraSmall"
                          style={{ color: '#595959', cursor: 'pointer' }}
                        />
                      </Tooltip>
                    </span>
                  }
                  key="email"
                  autoFocus
                  fullWidth
                  onPaste={(e) => {
                    e.preventDefault();
                    const text = e.clipboardData.getData('text').trim();
                    props.setFieldValue('email', text);
                  }}
                />

                <Button variant="primary" className="loginButton" type="submit">
                  {t('forgotPassword.submitButton')}
                </Button>
              </Form>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </AuthLayout>
  );
};

// Create FormikForgetPassword HOC with translation support
const createFormikForgetPassword = (t) =>
  withFormik({
    validationSchema: toFormikValidationSchema(forgotPasswordSchema(t)),
    mapPropsToValues: () => initialValues,
    handleSubmit: onFormSubmit,
    validateOnChange: false,
    validateOnBlur: true,
  })(ForgetPassword);

export default function ForgetPasswordWithNavigate(props) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Memoize FormikForgetPassword to avoid recreating HOC on every render
  const FormikForgetPassword = useMemo(
    () => createFormikForgetPassword(t),
    [t]
  );

  return <FormikForgetPassword {...props} navigate={navigate} t={t} />;
}
